/* eslint-disable @tanstack/query/no-unstable-deps, @tanstack/query/exhaustive-deps --
 * Pre-existing: this chunked-query orchestrator intentionally feeds the
 * `useQueries` results array into several dep arrays (and the per-window
 * queryKey omits the shared tz). Flagged by `@tanstack/query/flat-recommended`
 * after it was enabled repo-wide; unrelated to the calendar-not-connected
 * re-add fix. Tracked for a separate lint cleanup. */
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  useQueries,
  useQueryClient,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import moment from 'moment-timezone';
import {
  normalizeTimeZoneForAtsCalendarApi,
  type CalendarEvent,
  type CalendarEventsFetchResult,
  type CalendarUserInfo,
} from './calendarEventsApi';
import { fetchCalendarEventsForUserBatched } from './batchedCalendarFetch';
import {
  buildWeekChunks,
  chunkAtWeekOffset,
  rangeIsoForWeekStartKey,
} from './calendarChunkUtils';
import {
  stashRemovedInterviewerEvents,
  takeSoftCachedInterviewerEvents,
  peekSoftCachedCalendarAvailable,
  clearSoftInterviewerCacheEntry,
  REMOVED_INTERVIEWER_SOFT_CACHE_TTL_MS,
} from './removedInterviewerCalendarCache';

const CHUNK_GC_TIME_MS = 5 * 60 * 1000;

/**
 * Stable React Query key for one interviewer × one ISO-week bucket.
 * `from` / `to` are NOT part of the key — they are derived from `weekStartKey` + `timezone`
 * so the FullCalendar `datesSet` handshake does not duplicate fetches for the same week.
 */
export function buildUserWeekCalendarQueryKey(args: {
  userId: number;
  weekStartKey: string;
  timezone: string;
  atsJobId: number | null;
  /**
   * Edit-mode only. Appended as a 7th element ONLY when present so
   * non-buffer flows keep their length-6 keys (and existing cache
   * entries / the key-shape test stay valid). Two interview edits on the
   * same job must not share a cache row — one carries `buffer_kind`, the
   * other doesn't.
   */
  atsInterviewId?: number | null;
}): readonly unknown[] {
  const base = [
    'calendar-events',
    'user-week',
    args.userId,
    args.weekStartKey,
    args.timezone,
    args.atsJobId,
  ] as const;
  return args.atsInterviewId !== null &&
    args.atsInterviewId !== undefined &&
    args.atsInterviewId > 0
    ? [...base, args.atsInterviewId]
    : base;
}

/** Position of `timezone` in the key built above. */
const TIMEZONE_KEY_INDEX = 4;

/**
 * Keep the previous chunk visible while navigating weeks, but NOT across a
 * timezone change: the BE anchors each event to the timezone it was fetched
 * with, so replaying the old zone's rows under a new one mispositions every
 * block and splits all-day events across two days until the refetch lands.
 * Returning `undefined` shows the loading state instead of wrong times.
 */
export function pickCalendarChunkPlaceholder<T>(
  prev: T | undefined,
  prevQuery: { queryKey?: readonly unknown[] } | undefined,
  timezone: string
): T | undefined {
  const prevTimezone = prevQuery?.queryKey?.[TIMEZONE_KEY_INDEX];
  if (prevTimezone !== undefined && prevTimezone !== timezone) {
    return undefined;
  }
  return prev;
}

function sortIds(ids: number[]): number[] {
  return Array.from(new Set(ids))
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b);
}

function dedupeKey(evt: CalendarEvent, userId: number): string {
  return `${evt.id ?? ''}|${evt.start}|${evt.end}|${userId}`;
}

function isUserWeekChunkQueryKey(key: unknown): key is readonly unknown[] {
  return (
    Array.isArray(key) &&
    (key.length === 6 || key.length === 7) &&
    key[0] === 'calendar-events' &&
    key[1] === 'user-week' &&
    typeof key[2] === 'number' &&
    typeof key[3] === 'string' &&
    typeof key[4] === 'string' &&
    (key[5] === null || typeof key[5] === 'number')
  );
}

export interface CalendarChunksQueryDescriptor {
  userId: number;
  weekStartKey: string;
  chunkIndex: number;
}

export interface UseInterviewerCalendarChunksQueryArgs {
  userIds: number[];
  /** Any instant in the visible week — normalized to ISO week start in `timezone` */
  anchorWeekStartIso: string;
  timezone: string;
  /** Number of 7-day chunks to load from the anchor (inclusive of visible week) */
  prefetchWeeks: number;
  /** Additional weeks beyond `prefetchWeeks` to background-prefetch */
  lookaheadPrefetchWeeks?: number;
  atsJobId?: number;
  /** Edit-mode only — threaded so the BE flags own-buffer events. */
  atsInterviewId?: number;
  enabled?: boolean;
  /** Legacy: skip busy fetch when timezone is not resolved to a real IANA zone */
  isLocalTimezone?: boolean;
  removedUserTtlMs?: number;
  /**
   * Off for callers that mount and unmount this hook WITHIN one open session
   * (a wizard step you can step away from and back to). The default re-confirm
   * is meant for re-opening the wizard, not for step navigation, where it just
   * re-pulls busy the recruiter is already looking at.
   */
  refetchVisibleWeekOnMount?: boolean;
}

export interface UseInterviewerCalendarChunksQueryResult {
  mergedEvents: CalendarEvent[];
  /** True while any required visible-week (chunk 0) user query has no data and no soft cache */
  isBlockingBusyFetch: boolean;
  /** Any chunk query is currently fetching */
  isFetching: boolean;
  /** First error from chunk queries (for toast) */
  fetchError: Error | null;
  usersInfoById: Record<string, CalendarUserInfo>;
  queryDescriptors: CalendarChunksQueryDescriptor[];
}

export function useInterviewerCalendarChunksQuery({
  userIds,
  anchorWeekStartIso,
  timezone,
  prefetchWeeks,
  lookaheadPrefetchWeeks = 1,
  atsJobId,
  atsInterviewId,
  enabled = true,
  isLocalTimezone = false,
  removedUserTtlMs = REMOVED_INTERVIEWER_SOFT_CACHE_TTL_MS,
  refetchVisibleWeekOnMount = true,
}: UseInterviewerCalendarChunksQueryArgs): UseInterviewerCalendarChunksQueryResult {
  const queryClient = useQueryClient();
  const sortedUserIds = useMemo(() => sortIds(userIds), [userIds]);

  /** Same canonical name ATS uses (`Asia/Calcutta` → `Asia/Kolkata`) so RQ keys match prefetch + API. */
  const zoneForKeysAndRanges = useMemo(
    () => normalizeTimeZoneForAtsCalendarApi(timezone),
    [timezone]
  );

  const anchorAlignedIso = useMemo(
    () =>
      moment(anchorWeekStartIso)
        .tz(zoneForKeysAndRanges)
        .startOf('week')
        .startOf('day')
        .toISOString(),
    [anchorWeekStartIso, zoneForKeysAndRanges]
  );

  const effectivePrefetch = Math.min(Math.max(prefetchWeeks, 1), 6);
  const effectiveLookahead = Math.min(Math.max(lookaheadPrefetchWeeks, 0), 4);

  const chunks = useMemo(
    () =>
      buildWeekChunks(
        anchorAlignedIso,
        zoneForKeysAndRanges,
        effectivePrefetch
      ),
    [anchorAlignedIso, zoneForKeysAndRanges, effectivePrefetch]
  );

  const queryDescriptors = useMemo((): Array<
    CalendarChunksQueryDescriptor & {
      rangeStartIso: string;
      rangeEndIso: string;
    }
  > => {
    const out: Array<
      CalendarChunksQueryDescriptor & {
        rangeStartIso: string;
        rangeEndIso: string;
      }
    > = [];
    for (let ci = 0; ci < chunks.length; ci += 1) {
      const c = chunks[ci];
      for (const userId of sortedUserIds) {
        out.push({
          userId,
          weekStartKey: c.weekStartKey,
          chunkIndex: ci,
          rangeStartIso: c.rangeStartIso,
          rangeEndIso: c.rangeEndIso,
        });
      }
    }
    return out;
  }, [chunks, sortedUserIds]);

  const jobKey = typeof atsJobId === 'number' && atsJobId > 0 ? atsJobId : null;
  const interviewKey =
    typeof atsInterviewId === 'number' && atsInterviewId > 0
      ? atsInterviewId
      : null;

  const canRun =
    enabled &&
    !isLocalTimezone &&
    sortedUserIds.length > 0 &&
    chunks.length > 0;

  const queriesConfig = useMemo(
    () =>
      queryDescriptors.map((d) => ({
        queryKey: buildUserWeekCalendarQueryKey({
          userId: d.userId,
          weekStartKey: d.weekStartKey,
          timezone,
          atsJobId: jobKey,
          atsInterviewId: interviewKey,
        }),
        queryFn: async (ctx: QueryFunctionContext<readonly unknown[]>) => {
          const { queryKey, signal, client } = ctx;
          const rqState = client.getQueryState(queryKey);
          const rqData =
            client.getQueryData<CalendarEventsFetchResult>(queryKey);
          if (
            rqData !== null &&
            rqState &&
            rqState.status === 'success' &&
            !rqState.isInvalidated &&
            rqState.fetchStatus === 'idle'
          ) {
            return rqData;
          }
          const soft = takeSoftCachedInterviewerEvents(
            d.userId,
            d.weekStartKey,
            timezone
          );
          if (soft !== null) {
            const softAvailable = peekSoftCachedCalendarAvailable(
              d.userId,
              d.weekStartKey,
              timezone
            );
            return {
              events: soft,
              usersInfo: [
                {
                  id: d.userId,
                  ...(softAvailable !== undefined
                    ? { calendar_available: softAvailable }
                    : {}),
                },
              ] as CalendarUserInfo[],
            };
          }
          const { rangeStartIso, rangeEndIso } = rangeIsoForWeekStartKey(
            d.weekStartKey,
            zoneForKeysAndRanges
          );
          const res = await fetchCalendarEventsForUserBatched({
            userId: d.userId,
            weekStartKey: d.weekStartKey,
            startDate: rangeStartIso,
            endDate: rangeEndIso,
            timezone: zoneForKeysAndRanges,
            atsJobId: jobKey,
            atsInterviewId: interviewKey,
            signal,
          });
          return res;
        },
        enabled: canRun,
        staleTime: 0,
        gcTime: CHUNK_GC_TIME_MS,
        // Visible week (chunk 0) re-confirms on every remount so a calendar
        // reconnect/disconnect made elsewhere is reflected when the wizard
        // re-opens. Lookahead stays cached for snappy week navigation.
        refetchOnMount:
          d.chunkIndex === 0 && refetchVisibleWeekOnMount ? 'always' : false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        // No retries on poll timeout. Each cycle already polls 15× over
        // 30s; if the backend stays PENDING that long, retrying with a
        // fresh `fetch_uuid` 3× more (TQ default) just multiplies the
        // load without changing the outcome — the recruiter can manually
        // refresh if needed. sch-28: caught this thundering-herd loop in
        // the network tab when 12 parallel queries (3 users × 4 weeks)
        // all timed out.
        retry: false,
        // No keep-previous-data: while a new fetch is pending we must not hold
        // the prior week's/interviewer's events. Combined with the isFetching
        // guard in isBlockingBusyFetch, the spinner shows until the fresh
        // response arrives, then the real data renders — never stale events.
      })),
    [
      canRun,
      jobKey,
      interviewKey,
      queryDescriptors,
      timezone,
      zoneForKeysAndRanges,
      refetchVisibleWeekOnMount,
    ]
  );

  const results = useQueries({
    queries: queriesConfig,
  });

  const prevSortedRef = useRef<number[]>([]);

  useLayoutEffect(() => {
    const prev = prevSortedRef.current;
    const next = sortedUserIds;
    if (prev.length === 0) {
      prevSortedRef.current = next;
      return;
    }
    const removed = prev.filter((id) => !next.includes(id));
    if (removed.length > 0) {
      for (const uid of removed) {
        const matches = queryClient.getQueryCache().findAll({
          predicate: (q) => {
            const key = q.queryKey;
            return isUserWeekChunkQueryKey(key) && (key[2] as number) === uid;
          },
        });
        for (const q of matches) {
          const key = q.queryKey;
          if (!isUserWeekChunkQueryKey(key)) {
            continue;
          }
          const weekStartKey = key[3] as string;
          const { data, status } = q.state;
          if (
            status === 'success' &&
            data !== undefined &&
            typeof data === 'object' &&
            'events' in data
          ) {
            const typed = data as CalendarEventsFetchResult;
            // Preserve this user's `calendar_available` so a re-add within
            // the TTL restores the disconnected flag (else the soft-cache
            // synth `usersInfo` loses it and the chip reads as connected).
            const stashedAvailable = typed.usersInfo?.find(
              (u) => Number(u.id) === uid
            )?.calendar_available;
            stashRemovedInterviewerEvents(
              uid,
              weekStartKey,
              timezone,
              typed.events ?? [],
              removedUserTtlMs,
              stashedAvailable
            );
          }
        }
        queryClient.removeQueries({
          predicate: (q) => {
            const key = q.queryKey;
            return isUserWeekChunkQueryKey(key) && (key[2] as number) === uid;
          },
        });
      }
    }
    prevSortedRef.current = next;
  }, [queryClient, removedUserTtlMs, sortedUserIds, timezone]);

  useEffect(() => {
    if (queryDescriptors.length !== results.length) {
      return;
    }
    results.forEach((r, i) => {
      if (!r.isSuccess || !r.data) {
        return;
      }
      const d = queryDescriptors[i];
      if (!d) {
        return;
      }
      clearSoftInterviewerCacheEntry(d.userId, d.weekStartKey, timezone);
    });
  }, [queryDescriptors, results, timezone]);

  useEffect(() => {
    if (!canRun) {
      return;
    }
    const startOffset = effectivePrefetch;
    const endOffset = effectivePrefetch + effectiveLookahead;
    for (let w = startOffset; w < endOffset; w += 1) {
      const c = chunkAtWeekOffset(anchorAlignedIso, zoneForKeysAndRanges, w);
      for (const uid of sortedUserIds) {
        const qk = buildUserWeekCalendarQueryKey({
          userId: uid,
          weekStartKey: c.weekStartKey,
          timezone: zoneForKeysAndRanges,
          atsJobId: jobKey,
          atsInterviewId: interviewKey,
        });
        const softPrefetch = takeSoftCachedInterviewerEvents(
          uid,
          c.weekStartKey,
          timezone
        );
        if (softPrefetch !== null) {
          const softAvailable = peekSoftCachedCalendarAvailable(
            uid,
            c.weekStartKey,
            timezone
          );
          queryClient.setQueryData(qk, {
            events: softPrefetch,
            usersInfo: [
              {
                id: uid,
                ...(softAvailable !== undefined
                  ? { calendar_available: softAvailable }
                  : {}),
              },
            ] as CalendarUserInfo[],
          });
          continue;
        }
        const st = queryClient.getQueryState(qk);
        const existing =
          queryClient.getQueryData<CalendarEventsFetchResult>(qk);
        if (
          existing !== null &&
          st &&
          st.status === 'success' &&
          !st.isInvalidated &&
          st.fetchStatus === 'idle'
        ) {
          continue;
        }
        const { rangeStartIso, rangeEndIso } = rangeIsoForWeekStartKey(
          c.weekStartKey,
          zoneForKeysAndRanges
        );
        void queryClient.prefetchQuery({
          queryKey: qk,
          queryFn: ({ signal }) =>
            fetchCalendarEventsForUserBatched({
              userId: uid,
              weekStartKey: c.weekStartKey,
              startDate: rangeStartIso,
              endDate: rangeEndIso,
              timezone: zoneForKeysAndRanges,
              atsJobId: jobKey,
              atsInterviewId: interviewKey,
              signal,
            }),
          staleTime: 0,
          gcTime: CHUNK_GC_TIME_MS,
        });
      }
    }
  }, [
    anchorAlignedIso,
    canRun,
    effectiveLookahead,
    effectivePrefetch,
    jobKey,
    interviewKey,
    queryClient,
    sortedUserIds,
    timezone,
    zoneForKeysAndRanges,
  ]);

  const mergedEvents = useMemo(() => {
    const merged: CalendarEvent[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < queryDescriptors.length; i += 1) {
      const d = queryDescriptors[i];
      const r = results[i];
      let list: CalendarEvent[] = [];
      if (r?.isSuccess && r.data) {
        list = r.data.events ?? [];
      } else {
        const soft = takeSoftCachedInterviewerEvents(
          d.userId,
          d.weekStartKey,
          timezone
        );
        if (soft !== null) {
          list = soft;
        }
      }
      for (const evt of list) {
        const k = dedupeKey(evt, d.userId);
        if (seen.has(k)) {
          continue;
        }
        seen.add(k);
        const withUser =
          typeof evt.user_id === 'number' && Number.isFinite(evt.user_id)
            ? evt
            : { ...evt, user_id: d.userId };
        merged.push(withUser);
      }
    }
    return merged;
  }, [queryDescriptors, results, timezone]);

  const usersInfoById = useMemo(() => {
    const map: Record<string, CalendarUserInfo> = {};
    for (let i = 0; i < results.length; i += 1) {
      const r = results[i];
      if (!r?.isSuccess || !r.data) {
        continue;
      }
      for (const u of r.data.usersInfo) {
        const key = String(u.id);
        const prev = map[key];
        if (!prev) {
          map[key] = u;
          continue;
        }
        // `calendar_available` is a per-request-WINDOW flag — BE returns
        // it false only when THIS week-chunk's provider fetch errored
        // (`@events[email].instance_of?(String)`), NOT a stable "calendar
        // connected" property. A connected interviewer can be false in
        // one chunk and true in another. Without folding, the
        // last-iterated chunk wins, so a single bad window flips a
        // connected interviewer to "calendar not connected" even though
        // their events (merged across all chunks) are still visible —
        // the exact contradiction QA hit when adding interviewers from a
        // different week and navigating. Aggregate sticky-true: reachable
        // if ANY window explicitly came back `true`; disconnected only
        // when a real window said `false` and none said `true`. An
        // `undefined` flag (synthetic placeholder / soft-cache re-add) is
        // "no signal" and must NOT flip a real `false` to available —
        // that was the re-add regression where a re-selected disconnected
        // interviewer read as connected.
        const prevA = prev.calendar_available;
        const curA = u.calendar_available;
        const available =
          prevA === true || curA === true
            ? true
            : prevA === false || curA === false
              ? false
              : (prevA ?? curA);
        map[key] = { ...prev, ...u, calendar_available: available };
      }
    }
    return map;
  }, [results]);

  const fetchError = useMemo((): Error | null => {
    for (const r of results) {
      if (r.isError && r.error) {
        return r.error instanceof Error ? r.error : new Error(String(r.error));
      }
    }
    return null;
  }, [results]);

  const firstChunk = chunks[0];

  const isBlockingBusyFetch = useMemo(() => {
    if (!canRun || !firstChunk) {
      return false;
    }
    for (const uid of sortedUserIds) {
      const idx = queryDescriptors.findIndex(
        (d) => d.userId === uid && d.weekStartKey === firstChunk.weekStartKey
      );
      if (idx < 0) {
        return true;
      }
      const r = results[idx];
      const soft = takeSoftCachedInterviewerEvents(
        uid,
        firstChunk.weekStartKey,
        timezone
      );
      if (soft !== null) {
        continue;
      }
      if (!r) {
        return true;
      }
      // Keep the spinner up for the whole in-flight fetch of the visible week
      // — including a refetch over already-cached data (refetchOnMount:'always'
      // on chunk 0). Status stays 'success' during such a refetch, so relying on
      // isSuccess alone hides the spinner while the calendar_events request is
      // still pending and paints stale events. Block until the fetch resolves.
      if (r.isFetching) {
        return true;
      }
      if (r.isSuccess || r.isError) {
        continue;
      }
      return true;
    }
    return false;
  }, [canRun, firstChunk, queryDescriptors, results, sortedUserIds, timezone]);

  const isFetching = useMemo(
    () => results.some((r) => r.isFetching),
    [results]
  );

  return {
    mergedEvents,
    isBlockingBusyFetch,
    isFetching,
    fetchError,
    usersInfoById,
    queryDescriptors,
  };
}
